import { Base64 } from 'js-base64'
import path from 'path'

import { removeTrailingSlash, uriEncodePath } from '../pathUtils'

/**
 * Reperesents a route path where each segment is encoded with encodeURIComponent
 * Paths are normalized as follows:
 * - Leading relative specifiers are preserved (e.g. `./`, `../`, `../../`)
 * - Other paths are made absolute and prefixed with `/`
 * - Trailing slashes are removed
 *
 * Readonly, and does not mutate. Methods that modify the path returns a new
 * URIEncodedRoutePath instance.
 */
export class EncodedRoutePath {
  /** Normalized path where each segment is encoded with encodeURIComponent */
  #encodedPath: string

  /** Normalized, unencoded path */
  #path: string

  constructor(...segments: string[]) {
    this.#path = removeTrailingSlash(path.join(...segments))
    this.#encodedPath = uriEncodePath(this.#path)
  }

  /** Base 64 encoded path */
  public get base64encodedPath() {
    return Base64.encode(this.#path, true)
  }

  /** Relative Base 64 encoded path */
  public get base64encodedPathRelative() {
    return `./${this.base64encodedPath}`
  }

  /** Path where each segment is encoded with encodeURIComponent */
  public get encodedPath() {
    return this.#encodedPath
  }

  /** Original, unencoded path */
  public get path() {
    return this.#path
  }

  /** Removes n last segments from path */
  public pop(n = 1) {
    return new EncodedRoutePath(this.#path, '../'.repeat(n))
  }

  public toAbsolute() {
    if (this.#path.startsWith('./')) {
      return new EncodedRoutePath(this.#path.substring(1))
    }
    return this
  }

  public toRelative() {
    if (this.#path.startsWith('./')) {
      return this
    }
    return new EncodedRoutePath(`./${this.#path}`)
  }
  /** Returns object in JSON context */
  toJSON() {
    return {
      path: this.#path,
      encodedPath: this.encodedPath,
    }
  }

  /** Returns encoded path when used as a string */
  public toString() {
    return this.encodedPath
  }
}
